/**
 * Coalesces concurrent identical fetch() calls into one network request.
 * Each caller gets their own Response via clone() so .json() works per caller.
 *
 * Does NOT patch window.fetch (that caused double calls with HMR / load order).
 *
 * Skips deduplication when:
 * - init.signal is set (AbortController must stay per-request)
 * - body is FormData / Blob / ArrayBuffer / ReadableStream
 */

const ORIGINAL_FETCH_KEY = "__mermadic_original_fetch__";

function getNativeFetch() {
  if (typeof window === "undefined") {
    return fetch;
  }
  if (!window[ORIGINAL_FETCH_KEY]) {
    window[ORIGINAL_FETCH_KEY] = window.fetch.bind(window);
  }
  return window[ORIGINAL_FETCH_KEY];
}

const inflight = new Map();

function getUrlString(input) {
  if (typeof input === "string") return input;
  if (input && typeof input === "object" && "url" in input) return String(input.url);
  return "";
}

function shouldSkipDedupe(init) {
  if (init?.signal != null) return true;
  const b = init?.body;
  if (b == null || b === "") return false;
  if (typeof FormData !== "undefined" && b instanceof FormData) return true;
  if (typeof Blob !== "undefined" && b instanceof Blob) return true;
  if (typeof ArrayBuffer !== "undefined" && b instanceof ArrayBuffer) return true;
  if (typeof ReadableStream !== "undefined" && b instanceof ReadableStream) return true;
  return false;
}

function bodyKey(body) {
  if (body == null || body === "") return "";
  if (typeof body === "string") return body;
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
    return body.toString();
  }
  return String(body);
}

function makeKey(input, init = {}) {
  const url = getUrlString(input);
  const method = (init.method || "GET").toUpperCase();
  const body = bodyKey(init.body);
  return `${method}\u0000${url}\u0000${body}`;
}

/**
 * @param {RequestInfo} input
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export function dedupedFetch(input, init = {}) {
  const nf = getNativeFetch();

  if (shouldSkipDedupe(init)) {
    return nf(input, init);
  }

  const key = makeKey(input, init);
  let p = inflight.get(key);
  if (!p) {
    p = nf(input, init).finally(() => {
      inflight.delete(key);
    });
    inflight.set(key, p);
  }

  return p.then((response) => response.clone());
}
